import { useDisclosure } from '@mantine/hooks';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import CreateRevisionPredefinedDataModal from 'components/modals/create-revision-predefined-data-modal/CreateRevisionPredefinedDataModal';
import { RevisionPredefinedDataSelection } from 'components/inputs/revision-predefined-data/types';

interface OpenCreateRevisionPredefinedDataModalParams {
  initialName: string;
  deviceTypeName: string;
  predefinedDataTypeId: number;
  predefinedDataTypeName: string;
}

type OpenCreateRevisionPredefinedDataModal = (
  params: OpenCreateRevisionPredefinedDataModalParams
) => Promise<RevisionPredefinedDataSelection | null>;

interface ICreateRevisionPredefinedDataModalContext {
  openCreateRevisionPredefinedDataModal: OpenCreateRevisionPredefinedDataModal;
}

const CreateRevisionPredefinedDataModalContext = createContext<ICreateRevisionPredefinedDataModalContext>(undefined!);

/**
 * Provider for the revision predefined data creation modal.
 */
export function CreateRevisionPredefinedDataModalProvider({ children }: { children: React.ReactNode }) {
  const [opened, { close, open }] = useDisclosure(false);
  const [params, setParams] = useState<OpenCreateRevisionPredefinedDataModalParams | null>(null);
  const resolverRef = useRef<((value: RevisionPredefinedDataSelection | null) => void) | null>(null);

  const resolveAndClose = useCallback(
    (value: RevisionPredefinedDataSelection | null) => {
      resolverRef.current?.(value);
      resolverRef.current = null;
      close();
      setParams(null);
    },
    [close]
  );

  const openCreateRevisionPredefinedDataModal: OpenCreateRevisionPredefinedDataModal = useCallback(
    (nextParams) => {
      setParams(nextParams);
      open();

      return new Promise((resolve) => {
        resolverRef.current = resolve;
      });
    },
    [open]
  );

  const value = useMemo(
    () => ({
      openCreateRevisionPredefinedDataModal,
    }),
    [openCreateRevisionPredefinedDataModal]
  );

  return (
    <CreateRevisionPredefinedDataModalContext.Provider value={value}>
      {children}
      {opened && params && (
        <CreateRevisionPredefinedDataModal
          opened
          onClose={() => resolveAndClose(null)}
          onCreated={(createdValue) => resolveAndClose(createdValue)}
          {...params}
        />
      )}
    </CreateRevisionPredefinedDataModalContext.Provider>
  );
}

/**
 * Hook for opening the revision predefined data creation modal.
 */
export function useCreateRevisionPredefinedDataModal() {
  return useContext(CreateRevisionPredefinedDataModalContext);
}
